import type { ChangeEvent, ReactNode } from 'react';
import type { FixedCropperRef } from 'react-advanced-cropper';

import { ImagePlusIcon, RefreshCcwIcon, RotateCcwIcon, RotateCwIcon, Trash2Icon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FixedCropper, ImageRestriction, RectangleStencil } from 'react-advanced-cropper';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import 'react-advanced-cropper/dist/style.css';
import 'react-advanced-cropper/dist/themes/classic.css';

export function useImageCropper(defaultSrc?: string | Image) {
  const cropperRef = useRef<FixedCropperRef>(null);
  const [image, setImage] = useState<Image | null>(null);
  const getCroppedImage = useCallback(async (maxWidth: number = 256, maxHeight: number = 256): Promise<Blob | null> => {
    return new Promise((resolve, reject) => {
      if (!cropperRef.current) {
        reject('No cropper');
        return;
      }

      const canvas = cropperRef.current.getCanvas({
        minHeight: 1,
        minWidth: 1,
        maxHeight,
        maxWidth,
      });

      if (!canvas) {
        reject('No canvas');
        return;
      }

      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  }, []);

  const rotate = useCallback((angle: number) => {
    if (cropperRef.current) {
      cropperRef.current.rotateImage(angle);
    }
  }, []);

  const reset = useCallback(() => {
    if (cropperRef.current) {
      cropperRef.current.reset();
    }
  }, []);

  const remove = useCallback(() => {
    setImage(null);
  }, [setImage]);

  useEffect(() => {
    if (!defaultSrc) return;
    if (typeof defaultSrc === 'string') {
      setImage({ src: defaultSrc });
    } else {
      setImage(defaultSrc);
    }
  }, [defaultSrc, setImage]);

  return {
    image,
    setImage,
    cropperRef,
    getCroppedImage,
    rotate,
    reset,
    remove,
  };
}

export interface Image {
  type?: string;
  src: string;
}

export interface ImageCropperProps {
  defaultSrc?: string | Image;
  extraControls?: ReactNode;
  aspectRatio?: number;
  maxHeight?: number;
  maxWidth?: number;
  cropperControls: ReturnType<typeof useImageCropper>;
}

export function ImageCropper(imageCropperProps: ImageCropperProps) {
  const { defaultSrc, extraControls, aspectRatio = 1, maxHeight = 256, maxWidth = 256, cropperControls } = imageCropperProps;

  const { cropperRef, rotate, reset, setImage, remove, image } = cropperControls;
  const inputRef = useRef<HTMLInputElement>(null);
  const clickFileInput = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };
  const onLoadImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files[0]) {
      const blob = URL.createObjectURL(files[0]);

      setImage({
        src: blob,
        type: files[0].type,
      });
    }
    event.target.value = '';
  };

  useEffect(() => {
    if (!defaultSrc) return;
    if (typeof defaultSrc === 'string') {
      setImage({ src: defaultSrc });
    } else {
      setImage(defaultSrc);
    }
  }, [defaultSrc, setImage]);

  useEffect(() => {
    return () => {
      if (image && image.src && image.src.startsWith('blob:')) {
        URL.revokeObjectURL(image.src);
      }
    };
  }, [image]);

  return (
    <div className={'flex grow flex-col items-center gap-6 overflow-auto p-4'}>
      <input className={'hidden'} ref={inputRef} type={'file'} accept={'image/*'} onChange={onLoadImage} />
      <div className={'relative flex h-full max-h-[400px] min-h-[256px] w-full items-center justify-center overflow-hidden rounded-lg'}>
        {image?.src ? (
          <FixedCropper
            ref={cropperRef}
            className={'bg-muted min-h-full min-w-full'}
            src={image.src}
            crossOrigin={true}
            stencilProps={{
              grid: true,
              aspectRatio,
            }}
            stencilComponent={RectangleStencil}
            stencilSize={{ width: maxWidth, height: maxHeight }}
            imageRestriction={ImageRestriction.stencil}
          />
        ) : (
          <EmptyState>
            <EmptyStateCircles className={'z-0'} />
            <FeaturedIcon size={'lg'} className={'z-10'}>
              <ImagePlusIcon />
            </FeaturedIcon>
            <EmptyStateContent>
              <EmptyStateTitle>Nie wybrano zdjęcia</EmptyStateTitle>

              <EmptyStateFooter>
                <Button onClick={clickFileInput} variant={'outline'} name="upload initial image" aria-label="upload initial image" size={'sm'}>
                  Wybierz zdjęcie
                </Button>
              </EmptyStateFooter>
            </EmptyStateContent>
          </EmptyState>
        )}
      </div>
      <ButtonGroup className={'w-full max-w-90'}>
        <Button name="rotate left" aria-label="rotate left" className={'w-full'} variant={'outline'} onClick={() => rotate(-45)}>
          <RotateCcwIcon />
        </Button>
        <Button name="rotate right" aria-label="rotate right" className={'w-full'} variant={'outline'} onClick={() => rotate(45)}>
          <RotateCwIcon />
        </Button>
        <Button
          name="reset"
          aria-label="reset"
          className={'w-full'}
          variant={'outline'}

          onClick={() => reset()}
        >
          <RefreshCcwIcon />
        </Button>
        <Button name="upload image" aria-label="upload image" className={'w-full'} variant={'outline'} onClick={() => clickFileInput()}>
          <ImagePlusIcon />
        </Button>
        <Button name="delete avatar" aria-label="delete avatar" disabled={!image?.src} className={'w-full'} variant={'outline'} onClick={() => remove()}>
          <Trash2Icon className="stroke-brand!" />
        </Button>
        {extraControls}
      </ButtonGroup>
    </div>
  );
}
